/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import { useCallback, MutableRef, Ref } from 'preact/hooks';
import { getClientHints } from '../../utils/PRIVATE_clientHints';
import { Keys } from '../../utils/UNSAFE_keys';
import { SizingInfo, TableColumn } from '../Table';
import { handleRowActionGesture, handleSortActionGesture } from '../utils/TableActionUtils';
import { getElementFromCell } from '../utils/TableDomUtils';
import {
  handleAdjacentRowGesture,
  handleAdjacentColumnGesture,
  handlePageRowGesture,
  handleJumpColumnGesture
} from '../utils/TableNavigationUtils';
import { handleSelectionGesture, handleExtendSelectionGesture } from '../utils/TableSelectionUtils';
import { SelectionMode } from '../../UNSAFE_Collection';
import {
  Cell,
  CurrentCellDetail,
  RowActionDetail,
  SortCriterionDetail,
  TableSelectionDetail
} from '../../UNSAFE_TableView';

type UseKeyboardHandlingProps<K, D, C extends string> = {
  outerTableRef: Ref<HTMLDivElement>;
  tableId: string;
  data: D[] | null;
  isRtl: boolean;
  isTabbableMode: boolean;
  columnsArray: TableColumn<K, D, C>[];
  sizingInfoRef: MutableRef<SizingInfo>;
  selectionExtensionElementRef: MutableRef<HTMLElement | undefined>;
  onCurrentCellChanged: (detail: CurrentCellDetail<K, C>) => void;
  fullColumnWidths?: number[];
  currentCell?: Cell<K, C>;
  selectionMode?: { row?: SelectionMode; column?: SelectionMode };
  selected?: { row?: Keys<K>; column?: Keys<C> };
  onTableSelectionChange?: (
    detail: TableSelectionDetail<K, C>,
    isExtendableSelection: boolean
  ) => void;
  onRowAction?: (detail: RowActionDetail<K, D>) => void;
  sortCriterion?: { key: C; sortDirection: 'ascending' | 'descending' }[];
  onSortCriterionChange?: (detail: SortCriterionDetail<C>) => void;
};

/**
 * Hook that manages keyboard interactions on the Preact Table
 */
export function useKeyboardHandling<K extends string | number, D, C extends string>({
  outerTableRef,
  tableId,
  data,
  isRtl,
  isTabbableMode,
  columnsArray,
  sizingInfoRef,
  selectionExtensionElementRef,
  fullColumnWidths,
  currentCell,
  onCurrentCellChanged,
  selectionMode,
  selected,
  onTableSelectionChange,
  onRowAction,
  sortCriterion,
  onSortCriterionChange
}: UseKeyboardHandlingProps<K, D, C>) {
  // Keydown handler for the outer Table. This handles all internal
  // keyboard navigation for the Table.
  const _keyDownHandler = useCallback(
    (event: KeyboardEvent) => {
      const platform = getClientHints().platform;
      const keyHandlers: Record<string, (root: HTMLDivElement) => void> = {
        ' ': function (root: HTMLDivElement) {
          if (outerTableRef.current != null) {
            if (currentCell != null) {
              if (!isTabbableMode) {
                if (onTableSelectionChange != null) {
                  handleSelectionGesture(
                    outerTableRef.current,
                    tableId,
                    columnsArray,
                    getElementFromCell(root, tableId, currentCell) as HTMLElement,
                    true,
                    onTableSelectionChange,
                    selectionMode,
                    selected
                  );
                }
                event.preventDefault();
              }
              if (
                onRowAction != null &&
                data != null &&
                handleRowActionGesture(
                  outerTableRef.current,
                  tableId,
                  data,
                  onRowAction,
                  event.target as HTMLElement,
                  currentCell
                )
              ) {
                event.stopPropagation();
              }
            }
          }
        },
        Enter: function () {
          if (outerTableRef.current != null) {
            if (currentCell != null) {
              if (
                onRowAction != null &&
                data != null &&
                currentCell.type === 'data' &&
                handleRowActionGesture(
                  outerTableRef.current,
                  tableId,
                  data,
                  onRowAction,
                  event.target as HTMLElement,
                  currentCell
                )
              ) {
                event.stopPropagation();
              } else if (
                !isTabbableMode &&
                onSortCriterionChange != null &&
                currentCell.type === 'header' &&
                handleSortActionGesture(
                  columnsArray,
                  currentCell.columnKey,
                  onSortCriterionChange,
                  sortCriterion
                )
              ) {
                event.stopPropagation();
              }
            }
          }
        },
        ArrowUp: function (root: HTMLDivElement) {
          if (outerTableRef.current != null) {
            if (!isTabbableMode && currentCell != null) {
              if (
                currentCell.type === 'data' &&
                event.shiftKey &&
                onTableSelectionChange != null &&
                selectionMode?.row === 'multiple'
              ) {
                handleExtendSelectionGesture(
                  outerTableRef.current,
                  tableId,
                  columnsArray,
                  sizingInfoRef,
                  root,
                  true,
                  currentCell,
                  selectionExtensionElementRef,
                  onTableSelectionChange,
                  isRtl,
                  fullColumnWidths,
                  selectionMode,
                  selected
                );
              } else {
                handleAdjacentRowGesture(
                  root,
                  tableId,
                  columnsArray,
                  currentCell,
                  onCurrentCellChanged,
                  true
                );
              }
              event.preventDefault();
            }
          }
        },
        ArrowDown: function (root: HTMLDivElement) {
          if (outerTableRef.current != null) {
            if (!isTabbableMode && currentCell != null) {
              if (
                currentCell.type === 'data' &&
                event.shiftKey &&
                onTableSelectionChange != null &&
                selectionMode?.row === 'multiple'
              ) {
                handleExtendSelectionGesture(
                  outerTableRef.current,
                  tableId,
                  columnsArray,
                  sizingInfoRef,
                  root,
                  false,
                  currentCell,
                  selectionExtensionElementRef,
                  onTableSelectionChange,
                  isRtl,
                  fullColumnWidths,
                  selectionMode,
                  selected
                );
              } else {
                handleAdjacentRowGesture(
                  root,
                  tableId,
                  columnsArray,
                  currentCell,
                  onCurrentCellChanged,
                  false
                );
              }
              event.preventDefault();
            }
          }
        },
        ArrowLeft: function (root: HTMLDivElement) {
          if (outerTableRef.current != null) {
            if (!isTabbableMode && currentCell != null) {
              if (
                (currentCell.type === 'header' || currentCell.type === 'footer') &&
                event.shiftKey &&
                onTableSelectionChange != null &&
                selectionMode?.column === 'multiple'
              ) {
                handleExtendSelectionGesture(
                  outerTableRef.current,
                  tableId,
                  columnsArray,
                  sizingInfoRef,
                  root,
                  true,
                  currentCell,
                  selectionExtensionElementRef,
                  onTableSelectionChange,
                  isRtl,
                  fullColumnWidths,
                  selectionMode,
                  selected
                );
              } else {
                handleAdjacentColumnGesture(
                  columnsArray,
                  currentCell,
                  onCurrentCellChanged,
                  !isRtl
                );
              }
              event.preventDefault();
            }
          }
        },
        ArrowRight: function (root: HTMLDivElement) {
          if (outerTableRef.current != null) {
            if (!isTabbableMode && currentCell != null) {
              if (
                (currentCell.type === 'header' || currentCell.type === 'footer') &&
                event.shiftKey &&
                onTableSelectionChange != null &&
                selectionMode?.column === 'multiple'
              ) {
                handleExtendSelectionGesture(
                  outerTableRef.current,
                  tableId,
                  columnsArray,
                  sizingInfoRef,
                  root,
                  false,
                  currentCell,
                  selectionExtensionElementRef,
                  onTableSelectionChange,
                  isRtl,
                  fullColumnWidths,
                  selectionMode,
                  selected
                );
              } else {
                handleAdjacentColumnGesture(columnsArray, currentCell, onCurrentCellChanged, isRtl);
              }
              event.preventDefault();
            }
          }
        },
        PageUp: function (root: HTMLDivElement) {
          if (!isTabbableMode && currentCell != null) {
            handlePageRowGesture(
              root,
              tableId,
              columnsArray,
              sizingInfoRef,
              currentCell,
              onCurrentCellChanged,
              true,
              isRtl,
              fullColumnWidths
            );
            event.preventDefault();
          }
        },
        PageDown: function (root: HTMLDivElement) {
          if (!isTabbableMode && currentCell != null) {
            handlePageRowGesture(
              root,
              tableId,
              columnsArray,
              sizingInfoRef,
              currentCell,
              onCurrentCellChanged,
              false,
              isRtl,
              fullColumnWidths
            );
            event.preventDefault();
          }
        },
        Home: function (root: HTMLDivElement) {
          if (!isTabbableMode && currentCell != null) {
            handleJumpColumnGesture(
              root,
              tableId,
              columnsArray,
              currentCell,
              onCurrentCellChanged,
              true,
              platform === 'mac' ? event.metaKey : event.ctrlKey
            );
            event.preventDefault();
          }
        },
        End: function (root: HTMLDivElement) {
          if (!isTabbableMode && currentCell != null) {
            handleJumpColumnGesture(
              root,
              tableId,
              columnsArray,
              currentCell,
              onCurrentCellChanged,
              false,
              platform === 'mac' ? event.metaKey : event.ctrlKey
            );
            event.preventDefault();
          }
        }
      };
      if (Object.keys(keyHandlers).includes(event.key) && outerTableRef.current != null) {
        keyHandlers[event.key](outerTableRef.current);
      }
    },
    [
      outerTableRef,
      tableId,
      data,
      isRtl,
      isTabbableMode,
      columnsArray,
      sizingInfoRef,
      selectionExtensionElementRef,
      fullColumnWidths,
      currentCell,
      onCurrentCellChanged,
      selectionMode,
      selected,
      onTableSelectionChange,
      onRowAction,
      sortCriterion,
      onSortCriterionChange
    ]
  );

  return { onKeyDown: _keyDownHandler };
}
